/**
 * Caption/Subtitle Service
 * 
 * Displays TV-style captions at the bottom center of the screen.
 * Uses speed reading technique: previous word | current word | next word
 * Integrates with LemonFox TTS word timestamps.
 */

import { getCurrentWordTimestamps, getCurrentAudioElement, type WordTimestamp } from '../tts/index.js';
import { bus } from '../../core/events.js';

export interface CaptionSettings {
  enabled: boolean;
  size: 'small' | 'medium' | 'large' | 'xlarge';
}

export interface CaptionState {
  element: HTMLElement | null;
  previousWordElement: HTMLElement | null;
  currentWordElement: HTMLElement | null;
  nextWordElement: HTMLElement | null;
  updateInterval: number | null;
  settings: CaptionSettings;
}

let captionState: CaptionState = {
  element: null,
  previousWordElement: null,
  currentWordElement: null,
  nextWordElement: null,
  updateInterval: null,
  settings: {
    enabled: true,
    size: 'medium'
  }
};

// Load initial settings from localStorage
try {
  const saved = localStorage.getItem('pivot-settings');
  if (saved) {
    const parsed = JSON.parse(saved);
    if (parsed.captions) {
      captionState.settings = { ...captionState.settings, ...parsed.captions };
    }
  }
} catch (e) {
  console.warn('[Captions] Failed to load settings', e);
}

// Listen for settings changes
bus.on('settings.captions.changed', (payload: unknown) => {
  const data = payload as { settings: CaptionSettings; timestamp: number };
  if (data.settings) {
    updateSettings(data.settings);
  }
});

function updateSettings(newSettings: CaptionSettings): void {
  captionState.settings = { ...captionState.settings, ...newSettings };
  
  if (captionState.element) {
    // Apply size scaling
    let scale = 1.0;
    switch (newSettings.size) {
      case 'small': scale = 0.8; break;
      case 'medium': scale = 1.0; break;
      case 'large': scale = 1.25; break;
      case 'xlarge': scale = 1.5; break;
    }
    
    // Maintain centering while scaling
    captionState.element.style.transform = `translateX(-50%) scale(${scale})`;
    
    // Force update visibility
    if (!newSettings.enabled) {
      captionState.element.style.display = 'none';
    } else {
      // If enabled, updateCaptions will handle showing it if audio is playing
      const audio = getCurrentAudioElement();
      if (audio && !audio.paused && !audio.ended) {
        captionState.element.style.display = 'block';
      }
    }
  }
}

/**
 * Initialize caption element
 */
export function initializeCaptions(): void {
  if (captionState.element) {
    return; // Already initialized
  }

  // Create caption container
  const captionContainer = document.createElement('div');
  captionContainer.id = 'captions';
  captionContainer.style.cssText = `
    position: fixed;
    bottom: 20px; /* Centered in the 80px bottom gap (Desktop) */
    left: 50%;
    transform: translateX(-50%);
    background-color: rgba(0, 0, 0, 0.75);
    width: 600px;
    height: 40px;
    line-height: 40px; /* Vertical centering */
    border-radius: 4px;
    display: none;
    z-index: 10000;
    font-family: 'Courier New', monospace;
    font-size: 18px;
    color: white;
    white-space: nowrap;
    pointer-events: none;
    overflow: hidden;
  `;

  // Separators (absolute position to remain strictly fixed)
  const separator1 = document.createElement('span');
  separator1.textContent = '|';
  separator1.style.cssText = `
    position: absolute;
    left: 170px; /* 600px width: Left section ~170px (260 center) */
    top: 0;
    opacity: 0.4;
    pointer-events: none;
  `;

  const separator2 = document.createElement('span');
  separator2.textContent = '|';
  separator2.style.cssText = `
    position: absolute;
    right: 170px; /* 600px width: Right section ~170px */
    top: 0;
    opacity: 0.4;
    pointer-events: none;
  `;

  // Word containers (absolute position relative to container)
  // Layout Logic:
  // Container Width: 600px
  // Center Word Width: 260px
  // Center Position: Left 170px (Start) to 430px (End)
  // Separators: Left at 170px, Right at 430px (170px from right)
  // Side Words: Width 150px (20px padding + 150px width = 170px)
  
  const previousWord = document.createElement('span');
  previousWord.id = 'caption-previous';
  previousWord.style.cssText = `
    position: absolute;
    left: 10px;
    top: 0;
    width: 150px;
    text-align: right;
    opacity: 0.6;
    overflow: hidden;
    text-overflow: ellipsis;
  `;

  const currentWord = document.createElement('span');
  currentWord.id = 'caption-current';
  currentWord.style.cssText = `
    position: absolute;
    left: 170px;
    top: 0;
    width: 260px;
    text-align: center;
    font-weight: bold;
    overflow: hidden;
    text-overflow: ellipsis;
  `;

  const nextWord = document.createElement('span');
  nextWord.id = 'caption-next';
  nextWord.style.cssText = `
    position: absolute;
    right: 10px;
    top: 0;
    width: 150px;
    text-align: left;
    opacity: 0.6;
    overflow: hidden;
    text-overflow: ellipsis;
  `;

  captionContainer.appendChild(previousWord);
  captionContainer.appendChild(separator1); // Separator fixed on top
  captionContainer.appendChild(currentWord);
  captionContainer.appendChild(separator2); // Separator fixed on top
  captionContainer.appendChild(nextWord);

  document.body.appendChild(captionContainer);

  captionState.element = captionContainer;
  captionState.previousWordElement = previousWord;
  captionState.currentWordElement = currentWord;
  captionState.nextWordElement = nextWord;

  // Apply initial settings
  updateSettings(captionState.settings);

  // Auto-start caption updates (will show/hide based on audio availability)
  startCaptions();
}

/**
 * Update captions based on current audio position and word timestamps
 */
function updateCaptions(): void {
  const audio = getCurrentAudioElement();
  const wordTimestamps = getCurrentWordTimestamps();

  if (!audio || !wordTimestamps || wordTimestamps.length === 0 || !captionState.settings.enabled) {
    // Hide captions if no audio or timestamps or disabled
    if (captionState.element) {
      captionState.element.style.display = 'none';
    }
    return;
  }

  // Show captions
  if (captionState.element) {
    captionState.element.style.display = 'block'; // Block for absolute positioning context
  }

  const currentTime = audio.currentTime;

  // Find current word index
  let currentWordIndex = -1;
  for (let i = 0; i < wordTimestamps.length; i++) {
    const word = wordTimestamps[i];
    if (currentTime >= word.start && currentTime <= word.end) {
      currentWordIndex = i;
      break;
    }
  }

  // If no word found, check if we're before the first word or after the last
  if (currentWordIndex === -1) {
    if (currentTime < wordTimestamps[0].start) {
      currentWordIndex = -1; // Before first word
    } else if (currentTime > wordTimestamps[wordTimestamps.length - 1].end) {
      currentWordIndex = wordTimestamps.length; // After last word
    }
  }

  // RSVP (Rapid Serial Visual Presentation) style alignment
  // Calculate the optimal pivot point for the word (center of visual weight)
  // Usually around 35-40% into the word
  // We will simulate this by centering the whole word for now, 
  // but marking the center character in red as a visual anchor if requested.
  // For now, we'll just apply the specific color to the "Optimal Recognition Point" (ORP).
  
  const applyORP = (element: HTMLElement, word: string, isCurrent: boolean) => {
    if (!word) {
      element.textContent = '';
      return;
    }

    if (!isCurrent) {
      element.textContent = word;
      return;
    }

    // Calculate ORP index (approximate)
    // Logic inspired by Spritz:
    // 1 char: index 0
    // 2-5 chars: index 1
    // 6-9 chars: index 2
    // 10-13 chars: index 3
    // 14+ chars: index 4
    let orpIndex = 0;
    const len = word.length;
    if (len === 1) orpIndex = 0;
    else if (len >= 2 && len <= 5) orpIndex = 1;
    else if (len >= 6 && len <= 9) orpIndex = 2;
    else if (len >= 10 && len <= 13) orpIndex = 3;
    else orpIndex = 4;

    // If word is shorter than calculated index (shouldn't happen with above logic but safety check)
    if (orpIndex >= len) orpIndex = Math.floor((len - 1) / 2);

    element.innerHTML = '';
    
    const pre = document.createElement('span');
    pre.textContent = word.substring(0, orpIndex);
    
    const mid = document.createElement('span');
    mid.textContent = word.charAt(orpIndex);
    mid.style.color = '#ff4444'; // Red visual anchor
    mid.style.fontWeight = 'bold';
    
    const post = document.createElement('span');
    post.textContent = word.substring(orpIndex + 1);

    element.appendChild(pre);
    element.appendChild(mid);
    element.appendChild(post);
  };

  // Update word display
  if (captionState.previousWordElement && captionState.currentWordElement && captionState.nextWordElement) {
    // Previous word
    if (currentWordIndex > 0) {
      applyORP(captionState.previousWordElement, wordTimestamps[currentWordIndex - 1].word, false);
    } else {
      applyORP(captionState.previousWordElement, '', false);
    }

    // Current word
    if (currentWordIndex >= 0 && currentWordIndex < wordTimestamps.length) {
      applyORP(captionState.currentWordElement, wordTimestamps[currentWordIndex].word, true);
    } else {
      applyORP(captionState.currentWordElement, '', true);
    }

    // Next word
    if (currentWordIndex >= 0 && currentWordIndex < wordTimestamps.length - 1) {
      applyORP(captionState.nextWordElement, wordTimestamps[currentWordIndex + 1].word, false);
    } else {
      applyORP(captionState.nextWordElement, '', false);
    }
  }
}

/**
 * Start caption updates (auto-starts on initialization)
 */
export function startCaptions(): void {
  if (captionState.updateInterval) {
    return; // Already running
  }

  // Update every frame (60fps)
  captionState.updateInterval = window.setInterval(() => {
    updateCaptions();
  }, 1000 / 60);
}

/**
 * Stop caption updates
 */
export function stopCaptions(): void {
  if (captionState.updateInterval) {
    clearInterval(captionState.updateInterval);
    captionState.updateInterval = null;
  }

  // Hide captions
  if (captionState.element) {
    captionState.element.style.display = 'none';
  }
}

/**
 * Cleanup captions
 */
export function cleanupCaptions(): void {
  stopCaptions();
  
  if (captionState.element) {
    document.body.removeChild(captionState.element);
    captionState.element = null;
    captionState.previousWordElement = null;
    captionState.currentWordElement = null;
    captionState.nextWordElement = null;
  }
}

